import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Tabs,
  message
} from "antd";
import dayjs from "dayjs";
import { loadRuntimeSettings } from "@core/runtime-settings";
import {
  createSprPred,
  fetchSprPred,
  fetchSprPredLookups,
  saveSprPred
} from "./spr-pred-api";
import PredDialogs from "./pred-dialogs";
import PhoneInput, { emailRule, isEmailValid, isPhoneComplete, normalizePhone, phoneRule } from "./phone-input";
import {
  SEX_OPTIONS,
  STRANA_OPTIONS,
  emptyCard,
  normalizeCard,
  normalizeLookups
} from "./spr-pred-utils";

const LABEL_WIDTH = 160;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const toDay = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

/**
 * Форма контрагента. Delphi: UESprPred (MVP: 3 вкладки).
 */
const SprPredCard = ({ idPred, isNew = false, open, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lookups, setLookups] = useState({ vids: [], regions: [], socialGroups: [] });
  const [runtimeSettings, setRuntimeSettings] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    loadRuntimeSettings()
      .then((settings) => {
        if (!cancelled) setRuntimeSettings(settings);
      })
      .catch(() => {
        if (!cancelled) setRuntimeSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchSprPredLookups()
      .then((data) => {
        if (!cancelled) setLookups(normalizeLookups(data));
      })
      .catch(() => {
        if (!cancelled) setLookups({ vids: [], regions: [], socialGroups: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || runtimeSettings == null) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      const empty = emptyCard();
      form.setFieldsValue({
        ...empty,
        dateReg: toDay(empty.dateReg),
        dateBird: null,
        smsAgree: false
      });
      return () => {
        cancelled = true;
      };
    }

    if (idPred == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprPred(idPred)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeCard(row);
        form.setFieldsValue({
          ...card,
          dateReg: toDay(card.dateReg),
          dateBird: toDay(card.dateBird),
          region: card.region || 0,
          idSg: card.idSg || 0
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить контрагента");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idPred, form, runtimeSettings]);

  const phoneWatch = Form.useWatch("phone", form);
  const emailWatch = Form.useWatch("email", form);
  const contactsInvalid = !isPhoneComplete(phoneWatch) || !isEmailValid(emailWatch);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      vid: values.vid ?? 0,
      name: (values.name || "").trim(),
      adress: values.adress || "",
      adressFact: values.adressFact || "",
      inn: values.inn || "",
      city: values.city || "",
      phone: normalizePhone(values.phone),
      email: (values.email || "").trim(),
      komment: values.komment || "",
      region: values.region || 0,
      portalId: values.portalId || "",
      dateReg: values.dateReg ? values.dateReg.startOf("day").toISOString() : null,
      smsAgree: values.smsAgree ? 1 : 0,
      rs: values.rs || "",
      ks: values.ks || "",
      bik: values.bik || "",
      bank: values.bank || "",
      okpo: values.okpo || "",
      ogrn: values.ogrn || "",
      okato: values.okato || "",
      dateBird: values.dateBird ? values.dateBird.startOf("day").toISOString() : null,
      pasport: values.pasport || "",
      passportCode: values.passportCode || "",
      sex: values.sex ?? 0,
      idSg: values.idSg || 0,
      idStrana: values.idStrana ?? 0,
      workPlace: values.workPlace || "",
      workPosition: values.workPosition || "",
      prcExp: values.prcExp ?? null,
      director: values.director || "",
      buhgalter: values.buhgalter || ""
    };

    try {
      if (isNew) {
        const created = await createSprPred(body);
        message.success("Сохранено");
        onSaved?.(created);
      } else {
        const saved = await saveSprPred({ ...body, idPred });
        message.success("Сохранено");
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };
  if (loading || runtimeSettings == null) {
    return (
      <div className="spr-pred-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  const tabItems = [
    {
      key: "main",
      label: "Основные",
      forceRender: true,
      children: (
        <>
          {!isNew ? (
            <Form.Item label="#" name="idPred">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Вид" name="vid">
            <Select options={lookups.vids} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Юр. адрес" name="adress">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
          <Form.Item label="Факт. адрес" name="adressFact">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
          <Form.Item label="ИНН" name="inn">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Город" name="city">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Телефон" name="phone" rules={[phoneRule]} validateTrigger="onBlur">
            <PhoneInput />
          </Form.Item>
          <Form.Item label="Эл. почта" name="email" rules={[emailRule]} validateTrigger="onBlur">
            <Input maxLength={80} inputMode="email" placeholder="name@example.com" />
          </Form.Item>
          <Form.Item label="Комментарий" name="komment">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
          <Form.Item label="Регион" name="region">
            <Select options={lookups.regions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Портал #" name="portalId">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Регистрация" name="dateReg">
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="СМС" name="smsAgree" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item label="% комм." name="prcExp">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item label="Директор" name="director">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Бухгалтер" name="buhgalter">
            <Input maxLength={80} />
          </Form.Item>
        </>
      )
    },
    {
      key: "bank",
      label: "Реквизиты",
      forceRender: true,
      children: (
        <>
          <Form.Item label="Р/с" name="rs">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="К/с" name="ks">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="БИК" name="bik">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Банк" name="bank">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="ОКПО" name="okpo">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="ОГРН" name="ogrn">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="ОКАТО" name="okato">
            <Input maxLength={80} />
          </Form.Item>
        </>
      )
    },
    {
      key: "person",
      label: "Физ. лицо",
      forceRender: true,
      children: (
        <>
          <Form.Item label="Дата рожд." name="dateBird">
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} allowClear />
          </Form.Item>
          <Form.Item label="Паспорт" name="pasport">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
          <Form.Item label="Код подразд." name="passportCode">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Пол" name="sex">
            <Select options={SEX_OPTIONS} />
          </Form.Item>
          <Form.Item label="Соц. группа" name="idSg">
            <Select options={lookups.socialGroups} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Гражданство" name="idStrana">
            <Select options={STRANA_OPTIONS} />
          </Form.Item>
          <Form.Item label="Место работы" name="workPlace">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Должность" name="workPosition">
            <Input maxLength={80} />
          </Form.Item>
        </>
      )
    },
    ...(!isNew && idPred
      ? [
          {
            key: "dialogs",
            label: "Диалоги",
            children: <PredDialogs idPred={idPred} />
          }
        ]
      : [])
  ];

  return (
    <div className="spr-pred-card">
      <div className="spr-pred-card__body">
        {error ? (
          <Alert className="spr-pred-card__error" type="error" showIcon message={error} />
        ) : null}
        <Form
          form={form}
          className="spr-pred-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          <Tabs size="small" items={tabItems} />
        </Form>
      </div>
      <div className="spr-pred-card__footer">
        <Button
          type="primary"
          className="spr-pred-card__ok"
          loading={saving}
          disabled={contactsInvalid}
          title={contactsInvalid ? "Исправьте телефон или электронную почту" : undefined}
          onClick={() => form.submit()}
        >
          ОК
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprPredCard;
